import classes from './EditPlannedSurveyPage.module.css';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Paragraph } from '../../components/Paragraph';
import {
  SurveyFormCard,
  SurveyFormData,
} from '../../components/SurveyFormCard/SurveyFormCard';
import { useSelectedUnitContext } from '../../contexts/selectedUnitContext';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useGetTeamsQuery } from '../../hooks';
import { SurveyFormCardUtils } from '../../utils/SurveyFormCardUtils/SurveyFormCard.utils';
import { useGetPlannedSurveyById } from '../../hooks/queries/useGetPlannedSurveyByID';
import { useGetScheduledCustomSurveyById } from '../../hooks/queries/useGetScheduledCustomSurveyById';
import { toFormDateAndTime } from '../../components/SurveyFormCard/SurveyTimeline/surveyTimeline.utils';
import { GhostLoader } from '../../components/GhostLoader';
import { Alert } from '../../components/Alert/Alert';
import { useUpdatePlannedSurveyMutation } from '../../hooks/mutations/useUpdatePlannedSurvey';
import { useUpdateScheduledCustomSurveyMutation } from '../../hooks/mutations/useUpdateScheduledCustomSurvey';
import { PlannedSurvey } from '../../api/queries';
import { toast } from 'react-toastify';
import { Routes } from '../../enums/Routes';
import { useTranslation } from 'react-i18next';

export type { SurveyFormData };

export const EditPlannedSurveyPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id: surveyId } = useParams();
  const { selectedTeamId: teamId } = useSelectedUnitContext();
  const { data: teams } = useGetTeamsQuery();
  const [searchParams] = useSearchParams();
  const surveyType = searchParams.get('type') || 'standard';
  const isCustomSurvey = surveyType === 'custom';

  // Fetch standard survey (only if NOT type=custom)
  const {
    data: plannedSurvey,
    isLoading: isLoadingStandard,
    error: plannedSurveyError,
  } = useGetPlannedSurveyById(teamId, Number(surveyId), !isCustomSurvey);

  // Fetch custom survey (only if type=custom)
  const {
    data: scheduledCustomSurvey,
    isLoading: isLoadingCustom,
    error: customSurveyError,
  } = useGetScheduledCustomSurveyById(
    teamId,
    Number(surveyId),
    isCustomSurvey,
  );

  const { mutate: updatePlannedSurvey } = useUpdatePlannedSurveyMutation(teamId);
  const { mutate: updateScheduledCustomSurvey } =
    useUpdateScheduledCustomSurveyMutation(teamId);

  const [formData, setFormData] = useState<SurveyFormData | null>(null);
  const [error, setError] = useState<{ name: string; message: string } | null>(
    null,
  );

  const [currentStep, setCurrentStep] = useState<number>(0);

  const isLoading = isCustomSurvey ? isLoadingCustom : isLoadingStandard;
  const surveyError = isCustomSurvey ? customSurveyError : plannedSurveyError;

  // Initialize form data for standard survey
  useEffect(() => {
    if (!plannedSurvey || !teams || isCustomSurvey) return;
    if (formData) return;

    const surveyTypeValue =
      plannedSurvey.surveyType === 'WORK_ENVIRONMENT'
        ? 'work-environment'
        : 'psychological-safety';

    setFormData({
      team: SurveyFormCardUtils.teamAlreadySelected(teams, teamId),
      name: plannedSurvey.title,
      ...toFormDateAndTime(plannedSurvey.scheduledTime),
      isPlanning: !!plannedSurvey.scheduledTime,
      isRepeating: !!plannedSurvey.frequency,
      frequency: plannedSurvey.frequency ?? '',
      deadlineInDays: plannedSurvey.deadlineInDays ?? 1,
      daysBeforeDeadlineToNotify: plannedSurvey.daysBeforeDeadlineToNotify ?? 0,
      actionProposalDeadlineInDays:
        plannedSurvey.actionProposalDeadlineInDays ?? undefined,
      actionVotingDeadlineInDays:
        plannedSurvey.actionVotingDeadlineInDays ?? undefined,
      surveyType: surveyTypeValue,
      psychologicalSafetyTemplateId:
        surveyTypeValue === 'psychological-safety'
          ? plannedSurvey.templateId
          : undefined,
      workEnvironmentTemplateId:
        surveyTypeValue === 'work-environment'
          ? plannedSurvey.templateId
          : undefined,
    });
  }, [plannedSurvey, teams, teamId, formData, isCustomSurvey]);

  // Initialize form data for custom survey
  useEffect(() => {
    if (!scheduledCustomSurvey || !teams || !isCustomSurvey) return;
    if (formData) return;

    setFormData({
      team: SurveyFormCardUtils.teamAlreadySelected(teams, teamId),
      name: scheduledCustomSurvey.title,
      ...toFormDateAndTime(scheduledCustomSurvey.scheduledTime),
      isPlanning: !!scheduledCustomSurvey.scheduledTime,
      isRepeating: !!scheduledCustomSurvey.frequency,
      frequency: scheduledCustomSurvey.frequency ?? '',
      deadlineInDays: scheduledCustomSurvey.deadlineInDays,
      daysBeforeDeadlineToNotify: 0,
      surveyType: 'custom',
      customTemplateId: scheduledCustomSurvey.templateId,
    });
  }, [scheduledCustomSurvey, teams, teamId, formData, isCustomSurvey]);

  const onCreateSurvey = async (
    form: SurveyFormData,
    activeButton?: () => void,
  ) => {
    setError(null);
    const formDate: string =
      form.isPlanning && form.date ? form.date + 'T' + form.time : '';

    if (isCustomSurvey && scheduledCustomSurvey) {
      // Update custom survey
      const scheduledTime = new Date(formDate);

      updateScheduledCustomSurvey(
        {
          teamId,
          surveyId: Number(surveyId),
          survey: {
            title: form.name,
            description: scheduledCustomSurvey.description ?? form.name,
            templateId: form.customTemplateId ?? scheduledCustomSurvey.templateId,
            scheduledTime,
            frequency: form.frequency || undefined,
            deadlineInDays: form.deadlineInDays,
            minimumResponseRate: scheduledCustomSurvey.minimumResponseRate,
            allowAnonymous: scheduledCustomSurvey.allowAnonymous,
            isActive: scheduledCustomSurvey.isActive,
          },
        },
        {
          onSuccess: () => {
            setError(null);
            navigate(Routes.CurrentSurveys);
            toast.success(
              t('surveys.editPlannedPage.notifications.updateSuccess', {
                title: form.name,
              }),
              { position: 'bottom-right' },
            );
          },
          onError: (error: Error) => {
            setError({ name: error.name, message: error.message });
            toast.error(
              t('surveys.editPlannedPage.notifications.updateError'),
              { position: 'bottom-right' },
            );
            activeButton?.();
          },
        },
      );
    } else if (plannedSurvey) {
      // Update standard survey
      const templateId =
        form.surveyType === 'psychological-safety'
          ? form.psychologicalSafetyTemplateId
          : form.workEnvironmentTemplateId;

      const surveyTypeEnum =
        form.surveyType === 'work-environment'
          ? 'WORK_ENVIRONMENT'
          : 'PSYCHOLOGICAL_SAFETY';

      const updatedSurvey: {
        teamId: number;
        surveyId: number;
        survey: PlannedSurvey;
      } = {
        surveyId: Number(surveyId),
        teamId,
        survey: {
          id: Number(surveyId),
          title: form.name,
          description: form.name,
          templateId: templateId || plannedSurvey.templateId,
          scheduledTime: new Date(formDate),
          frequency: form.frequency,
          isActive: false,
          deadlineInDays: form.deadlineInDays || undefined,
          createdAt: '',
          surveyType: surveyTypeEnum,
          actionProposalDeadlineInDays:
            form.actionProposalDeadlineInDays ?? null,
          actionVotingDeadlineInDays: form.actionVotingDeadlineInDays ?? null,
          expectedParticipantsCount: plannedSurvey.expectedParticipantsCount,
          nextScheduledMeasurement: plannedSurvey.nextScheduledMeasurement,
        },
      };

      const onSuccess = () => {
        setError(null);
        navigate(Routes.CurrentSurveys);
        toast.success(
          t('surveys.editPlannedPage.notifications.updateSuccess', {
            title: updatedSurvey.survey.title,
          }),
          { position: 'bottom-right' },
        );
      };

      const onError = (error: Error) => {
        setError({ name: error.name, message: error.message });
        toast.error(t('surveys.editPlannedPage.notifications.updateError'), {
          position: 'bottom-right',
        });
        activeButton?.();
      };

      updatePlannedSurvey(updatedSurvey, {
        onSuccess,
        onError,
      });
    }
  };

  if (surveyError) {
    return (
      <div className={classes.container}>
        <Alert
          title={t('surveys.editPlannedPage.notFoundAlert')}
          variant="danger"
          size="lg"
        >
          <Paragraph size="md">{surveyError.message}</Paragraph>
        </Alert>
      </div>
    );
  }

  if (isLoading || !teams || !formData) {
    return (
      <div className={classes.container}>
        <GhostLoader style={{ height: 345, width: '100%', maxWidth: 560 }} />
      </div>
    );
  }

  return (
    <SurveyFormCard
      mode="edit"
      formData={formData}
      setFormData={setFormData as Dispatch<SetStateAction<SurveyFormData>>}
      currentStep={currentStep}
      setCurrentStep={setCurrentStep}
      teams={teams}
      onCreateSurvey={onCreateSurvey}
      backTo={Routes.PlannedSurveys}
      backLabel={t('surveys.editPlannedPage.backButton')}
      submitError={error}
    />
  );
};
